import { useCallback, useMemo, useState } from "react";

const normalizeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const createCartItem = (product, quantity = 1) => ({
  sparepartId: product.id,
  partName: product.partName || "Unnamed Part",
  partNumber: product.partNumber || "N/A",
  importerId: product.importerId || "",
  quantity: Math.max(1, normalizeNumber(quantity, 1)),
  unitCost: normalizeNumber(
    product.unitCost ?? product.purchasePrice ?? product.cost ?? 0
  ),
});

export default function usePurchaseOrderCart() {
  const [cart, setCart] = useState([]);

  const addToCart = useCallback((product, quantity = 1) => {
    if (!product?.id) return;

    setCart((current) => {
      const existingIndex = current.findIndex(
        (item) => String(item.sparepartId) === String(product.id)
      );

      if (existingIndex !== -1) {
        return current.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity:
                  item.quantity + Math.max(1, normalizeNumber(quantity, 1)),
              }
            : item
        );
      }

      return [...current, createCartItem(product, quantity)];
    });
  }, []);

  const addManyToCart = useCallback((products) => {
    if (!Array.isArray(products) || products.length === 0) return;

    setCart((current) => {
      const next = [...current];

      products.forEach((product) => {
        if (!product?.id) return;

        const index = next.findIndex(
          (item) => String(item.sparepartId) === String(product.id)
        );

        if (index !== -1) {
          next[index] = {
            ...next[index],
            quantity: next[index].quantity + 1,
          };
        } else {
          next.push(createCartItem(product));
        }
      });

      return next;
    });
  }, []);

  const removeFromCart = useCallback((sparepartId) => {
    setCart((current) =>
      current.filter(
        (item) => String(item.sparepartId) !== String(sparepartId)
      )
    );
  }, []);

  const updateQuantity = useCallback((sparepartId, quantity) => {
    const value = Math.max(1, normalizeNumber(quantity, 1));

    setCart((current) =>
      current.map((item) =>
        String(item.sparepartId) === String(sparepartId)
          ? { ...item, quantity: value }
          : item
      )
    );
  }, []);

  const increaseQuantity = useCallback(
    (sparepartId, amount = 1) => {
      setCart((current) =>
        current.map((item) =>
          String(item.sparepartId) === String(sparepartId)
            ? {
                ...item,
                quantity: item.quantity + amount,
              }
            : item
        )
      );
    },
    []
  );

  const decreaseQuantity = useCallback(
    (sparepartId, amount = 1) => {
      setCart((current) =>
        current.map((item) =>
          String(item.sparepartId) === String(sparepartId)
            ? {
                ...item,
                quantity: Math.max(1, item.quantity - amount),
              }
            : item
        )
      );
    },
    []
  );

  const updateUnitCost = useCallback((sparepartId, unitCost) => {
    const value = Math.max(0, normalizeNumber(unitCost, 0));

    setCart((current) =>
      current.map((item) =>
        String(item.sparepartId) === String(sparepartId)
          ? {
              ...item,
              unitCost: value,
            }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const removeItems = useCallback((ids) => {
    const idSet = new Set(ids.map(String));

    setCart((current) =>
      current.filter((item) => !idSet.has(String(item.sparepartId)))
    );
  }, []);

  const isInCart = useCallback(
    (sparepartId) =>
      cart.some(
        (item) => String(item.sparepartId) === String(sparepartId)
      ),
    [cart]
  );

  const getCartItem = useCallback(
    (sparepartId) =>
      cart.find(
        (item) => String(item.sparepartId) === String(sparepartId)
      ),
    [cart]
  );

  const totals = useMemo(() => {
    const quantityCount = cart.reduce(
      (sum, item) => sum + normalizeNumber(item.quantity),
      0
    );

    const subtotal = cart.reduce(
      (sum, item) =>
        sum +
        normalizeNumber(item.quantity) * normalizeNumber(item.unitCost),
      0
    );

    return {
      itemCount: cart.length,
      quantityCount,
      subtotal,
    };
  }, [cart]);

  return {
    cart,
    totals,

    addToCart,
    addManyToCart,
    removeFromCart,
    removeItems,

    updateQuantity,
    increaseQuantity,
    decreaseQuantity,
    updateUnitCost,

    clearCart,

    isInCart,
    getCartItem,
  };
}